import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Link from "components/ui/Link";
import { useTranslation } from "translation/useTranslation";
import { fetchSuggestedActions, getSuggestedActions } from "store/suggestedActionsSlice";
import { fetchSavedFilters, getSavedFilters } from "store/savedFiltersSlice";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { periodActivityTranslationFallback } from "helpers/periodActivityTranslations";
import "react-tooltip/dist/react-tooltip.css";
import { buildEmployeeImportConflictPath } from "../Imports/importNavigation";
import { getPermissions } from "store/userSlice";
import { canRunAction } from "./actionPermissions";

const actionTypeMeta = {
  create_balanced_scorecard: { key: 'action.type.create_balanced_scorecard', fallback: 'Leistungskarte anlegen' },
  create_evaluation: { key: 'action.type.create_evaluation', fallback: 'Beurteilung erstellen' },
  assign_to_period: { key: 'action.type.assign_to_period', fallback: 'Periode zuweisen' },
  activate_employees: { key: 'action.type.activate_employees', fallback: 'Aktivieren' },
  fill: { key: 'action.type.fill', fallback: 'Felder ausfüllen' },
  update_data: { key: 'action.type.update_data', fallback: 'Daten aktualisieren' },
  review_employee_import_conflicts: { key: 'action.type.review_employee_import_conflicts', fallback: 'Importkonflikte prüfen' },
};

const assignmentActionMeta = {
  budget: { key: 'action.type.assign_budget', fallback: 'Budget zuweisen' },
  group: { key: 'action.type.assign_group', fallback: 'Gruppe zuweisen' },
};

const metaFor = (action) => (
  action?.action_type === 'update_balanced_scorecard'
    ? assignmentActionMeta[action?.action_fields?.[0]]
    : actionTypeMeta[action?.action_type]
);

export const suggestedActionPath = action => {
  if (action?.source === "employee_import") {
    const importHashId = action?.action_context?.employee_import_hash_id || action.hash_id;
    return buildEmployeeImportConflictPath(importHashId);
  }

  return `/settings/actions/suggested/${action.hash_id}`;
};

function BudgetAction({ budget }) {
  const { editableTranslate } = useTranslation();

  const [show, setShow] = useState(false);

  return <div className="pt-1 mb-3">
    <div className="cursor-pointer" onClick={() => setShow(!show)} data-testid="budget-action-toggle">
      <i className={`bi bi-bold bi-chevron-${show ? 'up' : 'down'} mr-1`}></i>
      {editableTranslate('action.suggested.budget', 'Budget')}: <b>{budget.name.length > 20 ? budget.name.slice(0, 20) + '...' : budget.name}</b> <i className="bi bi-info-circle ml-1" data-tooltip-id={'suggested_budget_' + budget.hash_id + '_tooltip'}></i>
      <ReactTooltip id={'suggested_budget_' + budget.hash_id + '_tooltip'} clickable={true}>
        {editableTranslate('action.suggested.budget_source.tooltip', 'Quelle')}
        : <Link data-testid={`budget-calculation-link-${budget.hash_id}`} to={'/calculation/preliminary-check?hash_id=' + budget.hash_id} className="text-white text-underline">{editableTranslate('action.suggested.budget_calculation.tooltip', 'Berechnung')}</Link>
      </ReactTooltip>
    </div>
    {
      show && <div className="mt-2">
        {
          budget.suggested.map((act, j) => <div key={j}>
            <Link data-testid={`suggested-action-${act.hash_id}`}
              to={`/settings/actions/suggested/${act.hash_id}`}
              className="action-list-item d-flex"
            >
              <i className={`bi bi-bold bi-chevron-down mr-1 invisible`}></i>
              <span>
                <span className="font-weight-bold">{act.count}</span> {editableTranslate('calculation_overview.fix_' + act.translation_key, act.label)}
              </span>
            </Link>
          </div>)
        }
        <hr className="mt-1" />
      </div>
    }
  </div>;
}

export default function Action() {
  const dispatch = useDispatch();
  const { editableTranslate } = useTranslation();

  const suggestedActions = useSelector(getSuggestedActions);
  const savedFilters = useSelector(getSavedFilters);
  const permissions = useSelector(getPermissions);

  const systemFilters = useMemo(() => {
    if (!savedFilters) return [];

    return savedFilters?.filter((f) => f.type === 'system');
  }, [savedFilters]);

  useEffect(() => {
    dispatch(fetchSuggestedActions());
    dispatch(fetchSavedFilters());
  }, [dispatch]);

  const [standardSuggestedActions, budgetActions] = useMemo(() => {
    if (!suggestedActions) return [[], []];

    const tempArr = [];
    const tempArr2 = [];

    for (const act of suggestedActions) {
      if (act?.count <= 0) continue;
      // Hide actions the user may not run; the API would refuse them (LIX-1777).
      if (!canRunAction(act?.action_type, permissions)) continue;

      if (!act?.budget_setting) tempArr.push(act);
      else {
        const { budget_setting, ...obj } = act;

        let foundIndex = -1;
        for (let i = 0; i < tempArr2.length; i++) {
          if (tempArr2[i]?.hash_id === budget_setting?.hash_id) {
            foundIndex = i;
            break;
          }
        }

        if (foundIndex === -1) {
          tempArr2.push({
            hash_id: act?.budget_setting?.hash_id,
            name: act?.budget_setting?.name,
            suggested: [obj]
          });
        } else {
          tempArr2[foundIndex].suggested.push(obj);
        }
      }
    }

    return [tempArr, tempArr2];
  }, [suggestedActions, permissions]);

  return (
    <>
      <div className="col-lg col-md-9 section-col-right d-flex">
        <div className="bg-white w-100 p-4 content-card">
          <h4>{editableTranslate('global.menu.settings.actions', 'Aktionen')}</h4>
          <p className="section-description mb-4">{editableTranslate('action.description', 'Verwalten Sie vorgeschlagene Aktionen, Systemfilter und benutzerdefinierte Aktionen.')}</p>

          <div className="row row-eq-height">
            <div className="col-md-4 mb-4">
              <div className="balancescorecard-setting-card pb-3 h-100">
                <div className="card-setting-header border-bottom">
                  <h5>{editableTranslate('action.suggested.title', 'Vorgeschlagene Aktionen')}</h5>
                </div>
                <div className="card-setting-body mt-3">
                  {
                    budgetActions.map((budget, i) => <BudgetAction key={i} budget={budget} />)
                  }
                  {
                    standardSuggestedActions.map((action) => {
                      const meta = metaFor(action);
                      return <Link
                        key={action.hash_id}
                        to={suggestedActionPath(action)}
                        className="action-list-item"
                        data-testid={action.source === "employee_import" ? `employee-import-conflicts-action-${action.hash_id}` : undefined}
                      >
                        {meta && <>
                          <span>
                            {editableTranslate(meta.key, meta.fallback)}
                          </span>
                          <span> - </span>
                        </>}
                        <span>
                          <span className="font-weight-bold">{action.count}</span> {editableTranslate(action.translation_key, periodActivityTranslationFallback(action.translation_key, action.label))}
                        </span>
                      </Link>;
                    })
                  }
                  {
                    (standardSuggestedActions.length === 0 && budgetActions.length === 0) && <div className="text-muted">{editableTranslate('action.suggested.empty', 'Keine vorgeschlagenen Aktionen')}</div>
                  }
                </div>
              </div>
            </div>

            <div className="col-md-4 mb-4">
              <div className="balancescorecard-setting-card pb-3 h-100">
                <div className="card-setting-header border-bottom">
                  <h5>{editableTranslate('action.system_filters.title', 'Systemfilter')}</h5>
                </div>
                <div className="card-setting-body mt-3">
                  {
                    systemFilters && systemFilters.length > 0
                      ? systemFilters.map((filter) => (
                        <Link data-testid={`system-filter-${filter.hash_id}`}
                          key={filter.hash_id}
                          to={`/settings/actions/system-filter/${filter.hash_id}`}
                          className="action-list-item"
                        >
                          {editableTranslate(filter.translation_key, periodActivityTranslationFallback(filter.translation_key, filter.name))}
                        </Link>
                      ))
                      : <div className="text-muted">{editableTranslate('action.system_filters.empty', 'Keine Systemfilter')}</div>
                  }
                </div>
              </div>
            </div>

            <div className="col-md-4 mb-4">
              <div className="balancescorecard-setting-card pb-3 h-100">
                <div className="card-setting-header border-bottom">
                  <h5>{editableTranslate('action.custom.title', 'Benutzerdefinierte Filter')}</h5>
                </div>
                <div className="card-setting-body d-flex h-100 pb-3 justify-content-center align-items-center">
                  <Link data-testid="setup-filters-link" to="/settings/actions/custom" className="action-list-item action-custom-tile">
                    <i className="bi bi-arrow-right pr-2"></i> {editableTranslate('action.custom.setup_filters', 'Filter konfigurieren')}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
